// pages/DriverMapScreen.js
// Driver route intelligence map — fuel-adjusted stop quality visualization.
// Original IP — LoadDash Services LLC

import React, { useState, useEffect } from "react";
import { NAVY, NAVY2, BLUE, GREEN, RED, AMBER, STEEL, MUTED, BORDER, TEXT2, BG, BRAND_NAME } from "../constants";
import { LOAD_TIERS } from "../constants";
import { calcSplit } from "../utils/marketConfig";
import { supabase } from "../services/supabaseService";
import { DISPLAY_TIER_LEU_MAP, jobLEU, fmtLEU } from "../utils/leuUtils";

// ── Visual tokens (match DriverScreen) ───────────────────────
const DS = { H_PAD: 18, CARD_RADIUS: 14, LABEL: 10, MICRO: 9, BODY_SM: 11 };
const C = { background: "#fff", borderRadius: DS.CARD_RADIUS, border: `1px solid ${BORDER}`, padding: 14, marginBottom: 10, boxShadow: "0 1px 6px rgba(10,22,40,.04)" };

// ── Defaults ─────────────────────────────────────────────────
const FALLBACK_MARKET = "ut-hill-afb";

function jobMarket(job) { return job?.market_id || FALLBACK_MARKET; }

function tierLabel(key) {
  if (key === "Half") return "Medium";
  if (key === "ThreeQ") return "Large";
  const t = LOAD_TIERS.find(t => t.key === key);
  return t ? t.label.replace(" Load", "") : key || "Unknown";
}

function tierPrice(key) {
  return LOAD_TIERS.find(t => t.key === key)?.price || 0;
}

function stopQuality(grossPay, marketId, zoneKey) {
  const split = calcSplit(grossPay, marketId, zoneKey);
  const ratio = grossPay > 0 ? split.driverPay / grossPay : 0;
  const cls = ratio >= 0.80 ? "good" : ratio >= 0.60 ? "caution" : "weak";
  return { fuel: split.fuelEst, net: split.driverPay, ratio, cls };
}

const Q_COLORS = { good: GREEN, caution: AMBER, weak: RED };
const Q_LABELS = { good: "Good Pay", caution: "Okay Pay", weak: "Low Pay" };
const Q_BG = { good: "#e8f5e9", caution: "#fff8e1", weak: "#fce4e4" };

export default function DriverMapScreen({ session, role, onBackToRoute }) {
  const [jobs, setJobs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [mpg, setMpg] = useState(DEFAULT_MPG);
  const [gas, setGas] = useState(DEFAULT_GAS);
  const [selected, setSelected] = useState(null);

  const userId = session?.user?.id;

  useEffect(() => { loadJobs(); }, []);

  async function loadJobs() {
    setLoading(true);
    try {
      let query = supabase
        .from("requests")
        .select("*, payments(*)")
        .in("status", ["pending", "scheduled", "en_route", "in_progress", "complete"])
        .order("created_at", { ascending: true });
      if (role === "driver" && userId) query = query.eq("assigned_driver", userId);
      const { data, error } = await query;
      if (error) throw error;
      setJobs(data || []);
      if (data?.length > 0) setSelected(data[0]);
    } catch { }
    setLoading(false);
  }

  const activeJobs = jobs.filter(j => j.status !== "complete");
  const completedJobs = jobs.filter(j => j.status === "complete");

  // Pin positions — distribute evenly across mock map
  const pinPositions = [
    { top: "18%", left: "22%" }, { top: "35%", left: "58%" }, { top: "52%", left: "30%" },
    { top: "25%", left: "75%" }, { top: "65%", left: "65%" }, { top: "45%", left: "15%" },
    { top: "70%", left: "45%" }, { top: "30%", left: "42%" },
  ];

  return (
    <div style={{ paddingBottom: 80, background: BG, minHeight: "100vh" }}>
      {/* Header */}
      <div style={{ background: `linear-gradient(135deg,${NAVY},${NAVY2})`, padding: `20px ${DS.H_PAD}px 12px` }}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 6 }}>
          <div style={{ fontSize: DS.LABEL, fontWeight: 700, letterSpacing: 2.5, textTransform: "uppercase", color: BLUE }}>{BRAND_NAME} Driver</div>
          <div style={{ display: "flex", alignItems: "center", gap: 5 }}>
            <div style={{ width: 6, height: 6, borderRadius: "50%", background: "#4caf50" }} />
            <span style={{ fontSize: DS.MICRO, fontWeight: 700, color: "rgba(255,255,255,.55)" }}>Online</span>
          </div>
        </div>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 6 }}>
          <h2 style={{ fontWeight: 900, fontSize: 18, color: "#fff", margin: 0, lineHeight: 1.1 }}>Map</h2>
          {onBackToRoute && <button onClick={onBackToRoute} style={{ padding: "6px 12px", borderRadius: 8, border: "1px solid rgba(255,255,255,.2)", background: "rgba(255,255,255,.08)", color: "#42a5f5", fontWeight: 700, fontSize: DS.BODY_SM, cursor: "pointer", fontFamily: "inherit" }}>Route</button>}
        </div>
        <div style={{ fontSize: DS.BODY_SM, color: "rgba(255,255,255,.5)" }}>How good each stop is for your truck</div>
      </div>

      {/* Vehicle controls */}
      <div style={{ background: "#f0f2f5", padding: `8px ${DS.H_PAD}px`, display: "flex", justifyContent: "space-between", alignItems: "center", borderBottom: `1px solid ${BORDER}`, gap: 8 }}>
        <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
          <span style={{ fontSize: DS.BODY_SM, fontWeight: 600, color: STEEL }}>Vehicle: Pickup</span>
        </div>
        <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
          <div style={{ display: "flex", alignItems: "center", gap: 3 }}>
            <span style={{ fontSize: DS.LABEL, fontWeight: 600, color: STEEL }}>MPG</span>
            <input value={mpg} onChange={e => setMpg(parseFloat(e.target.value) || 1)} type="number" style={{ width: 40, padding: "3px 5px", borderRadius: 6, border: `1px solid ${BORDER}`, fontSize: DS.BODY_SM, fontWeight: 700, color: NAVY, textAlign: "center", fontFamily: "inherit" }} />
          </div>
          <div style={{ display: "flex", alignItems: "center", gap: 3 }}>
            <span style={{ fontSize: DS.LABEL, fontWeight: 600, color: STEEL }}>Gas $</span>
            <input value={gas} onChange={e => setGas(parseFloat(e.target.value) || 0)} type="number" step="0.01" style={{ width: 48, padding: "3px 5px", borderRadius: 6, border: `1px solid ${BORDER}`, fontSize: DS.BODY_SM, fontWeight: 700, color: NAVY, textAlign: "center", fontFamily: "inherit" }} />
          </div>
        </div>
      </div>

      {/* Map area */}
      <div style={{ position: "relative", height: 280, background: "#e8eef6", overflow: "hidden" }}>
        {/* Grid texture */}
        <div style={{ position: "absolute", inset: 0, opacity: 0.06, backgroundImage: "linear-gradient(rgba(0,0,0,.15) 1px, transparent 1px), linear-gradient(90deg, rgba(0,0,0,.15) 1px, transparent 1px)", backgroundSize: "40px 40px" }} />
        {/* Roads */}
        <div style={{ position: "absolute", width: "85%", height: 3, top: "42%", left: "8%", background: "rgba(10,22,40,.1)", borderRadius: 2 }} />
        <div style={{ position: "absolute", width: 3, height: "65%", top: "15%", left: "40%", background: "rgba(10,22,40,.1)", borderRadius: 2 }} />
        <div style={{ position: "absolute", width: "60%", height: 3, top: "68%", left: "22%", background: "rgba(10,22,40,.1)", borderRadius: 2 }} />

        {/* Pins */}
        {loading ? (
          <div style={{ position: "absolute", inset: 0, display: "flex", alignItems: "center", justifyContent: "center", color: STEEL, fontSize: 13 }}>Loading stops...</div>
        ) : (
          [...activeJobs, ...completedJobs].map((job, i) => {
            const pos = pinPositions[i % pinPositions.length];
            const price = tierPrice(job.size);
            const q = stopQuality(price, jobMarket(job), job.zone);
            const isComplete = job.status === "complete";
            const isSel = selected?.id === job.id;
            return (
              <div key={job.id} onClick={() => setSelected(job)} style={{
                position: "absolute", ...pos,
                width: isSel ? 32 : 28, height: isSel ? 32 : 28,
                borderRadius: "50%",
                background: isComplete ? "rgba(0,137,123,.4)" : Q_COLORS[q.cls],
                border: `2.5px ${isComplete ? "dashed" : "solid"} #fff`,
                display: "flex", alignItems: "center", justifyContent: "center",
                fontSize: 10, fontWeight: 800, color: "#fff",
                boxShadow: isSel ? "0 0 0 3px rgba(21,101,192,.4), 0 2px 8px rgba(0,0,0,.25)" : "0 2px 8px rgba(0,0,0,.2)",
                cursor: "pointer", transition: "all .15s", zIndex: isSel ? 5 : 1,
                opacity: isComplete ? 0.5 : 1,
              }}>
                {isComplete ? "✓" : i + 1}
              </div>
            );
          })
        )}
      </div>

      {/* Legend */}
      <div style={{ padding: `8px ${DS.H_PAD}px`, display: "flex", gap: 12, borderBottom: `1px solid ${BORDER}` }}>
        {[["good", "Good Pay"], ["caution", "Okay Pay"], ["weak", "Low Pay"]].map(([k, l]) => (
          <div key={k} style={{ display: "flex", alignItems: "center", gap: 4 }}>
            <div style={{ width: 8, height: 8, borderRadius: "50%", background: Q_COLORS[k] }} />
            <span style={{ fontSize: DS.LABEL, color: STEEL, fontWeight: 600 }}>{l}</span>
          </div>
        ))}
        <div style={{ display: "flex", alignItems: "center", gap: 4 }}>
          <div style={{ width: 8, height: 8, borderRadius: "50%", background: GREEN, opacity: 0.4, border: `1px dashed ${STEEL}` }} />
          <span style={{ fontSize: DS.LABEL, color: STEEL, fontWeight: 600 }}>Done</span>
        </div>
      </div>

      {/* Bottom sheet — selected stop */}
      {selected && (() => {
        const price = tierPrice(selected.size);
        const leu = jobLEU(selected);
        const q = stopQuality(price, jobMarket(job), job.zone);
        const perLEU = leu && leu > 0 ? q.net / leu : 0;
        return (
          <div style={{ background: "#fff", borderRadius: "16px 16px 0 0", padding: `16px ${DS.H_PAD}px`, borderTop: `1px solid ${BORDER}`, boxShadow: "0 -4px 20px rgba(10,22,40,.06)" }}>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: 8 }}>
              <div>
                <div style={{ fontWeight: 700, fontSize: 15, color: NAVY, marginBottom: 2 }}>{selected.addr || "—"}</div>
                <div style={{ fontSize: DS.BODY_SM, color: STEEL }}>{tierLabel(selected.size)} · {fmtLEU(leu)} LEU</div>
              </div>
              <span style={{ fontSize: DS.MICRO, fontWeight: 700, padding: "3px 9px", borderRadius: 20, background: Q_BG[q.cls], color: Q_COLORS[q.cls] }}>{Q_LABELS[q.cls]}</span>
            </div>
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 6, marginBottom: 8 }}>
              <div style={{ textAlign: "center", background: BG, borderRadius: 8, padding: 8 }}>
                <div style={{ fontWeight: 800, fontSize: 13, color: NAVY }}>${price}</div>
                <div style={{ fontSize: DS.MICRO, color: STEEL, marginTop: 2 }}>Gross</div>
              </div>
              <div style={{ textAlign: "center", background: BG, borderRadius: 8, padding: 8 }}>
                <div style={{ fontWeight: 800, fontSize: 13, color: q.fuel > 0 ? AMBER : STEEL }}>${q.fuel.toFixed(2)}</div>
                <div style={{ fontSize: DS.MICRO, color: STEEL, marginTop: 2 }}>Fuel Est.</div>
              </div>
              <div style={{ textAlign: "center", background: BG, borderRadius: 8, padding: 8 }}>
                <div style={{ fontWeight: 800, fontSize: 13, color: q.net >= 0 ? GREEN : RED }}>${q.net.toFixed(2)}</div>
                <div style={{ fontSize: DS.MICRO, color: STEEL, marginTop: 2 }}>Net Est.</div>
              </div>
            </div>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
              <span style={{ fontSize: DS.BODY_SM, fontWeight: 700, color: q.net >= 0 ? NAVY : RED }}>${perLEU.toFixed(2)} / LEU</span>
              <button onClick={() => {
                const url = (/iPhone|iPad|iPod/.test(navigator.userAgent))
                  ? "maps://maps.apple.com/?daddr=" + encodeURIComponent(selected.addr)
                  : "https://maps.google.com/?daddr=" + encodeURIComponent(selected.addr);
                window.open(url, "_blank");
              }} style={{ padding: "6px 11px", borderRadius: 8, border: "none", background: BLUE, color: "#fff", fontWeight: 700, fontSize: DS.BODY_SM, cursor: "pointer", fontFamily: "inherit" }}>Navigate to Stop</button>
            </div>
            {q.net < 0 && (
              <div style={{ marginTop: 8, padding: "8px 10px", borderRadius: 8, background: "#fce4e4", fontSize: DS.BODY_SM, fontWeight: 600, color: RED, textAlign: "center" }}>This stop costs more in fuel than it pays</div>
            )}
          </div>
        );
      })()}
    </div>
  );
}
